// Realtime notes for the הערות tab — a PORT of the business logic in
// src/components/_dashboard/rentalToSale/useSellerNotes.ts (per the CRM revamp
// rule: reuse the logic, never the legacy component/hook), extended with the
// revamp capabilities: pin/unpin, voice notes (audioUrl), and mirroring every
// update into the SHARED sessionCache key `notes:{contactId}` so the overview
// quick-panel and the tab badge read the exact same live data.
//
// Preserved legacy invariants (see the capability inventory in the task report):
//   - own-doc lookup by (userId, contactId) query — legacy docs have
//     auto-generated ids, so `.doc(deterministicId)` alone would miss them;
//     new docs are created with the deterministic id the backend also uses;
//   - org-shared teammates' notes via the doc-level organizationIds
//     array-contains listener, re-filtered per note (the doc field is a
//     union), strictly read-only;
//   - doc-level organizationIds is recomputed on every write and REMOVED
//     (never `[]`) when nothing is shared;
//   - whole-doc set() writes; mutators only touch the user's OWN notes and
//     spread untouched notes as-is, so unknown per-note fields survive;
//   - editing a note's text bumps its noteTime (legacy write shape). Pin and
//     share toggles intentionally do NOT bump it (metadata, not content).
//
// Write-safety invariants (whole-doc set() means a wrong base = data loss):
//   - every mutation resolves its base from the LATEST local state (a ref) —
//     never from a render closure — so a slow voice-note save can't erase a
//     text note added while it was uploading;
//   - a mutation that runs before the own-doc listener has hydrated for the
//     target contact (cold start, listener error, or a stale closure firing
//     after an in-place contact switch) does a one-shot read-modify-write
//     against Firestore instead of trusting empty/foreign React state;
//   - the target doc id is bound to the contactId the mutation started with,
//     so a save landing after a contact switch can never write one contact's
//     notes into another contact's doc;
//   - mutations are serialized through a queue (whole-doc writes from the same
//     client must not interleave).
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { db, storage } from "firebaseConfig";
import useAuth from "hooks/useAuth";
import useFirestore from "hooks/useFirestore";
import { getCached, setCached } from "../../data/sessionCache";
import { computeDocOrgIds, notesCacheKey, parseNoteTime, sortNotesNewestFirst } from "./notesData";
import { USER_NOTES_COLLECTION_NAME, userNotesDocId } from "../../../../../../@types/sharedSchema";
import type { UserNote, UserNotesDocument } from "../../../../../../@types/sharedSchema";
import type { DisplayUserNote } from "hooks/useListingNotes";
import { applyNoteShare, withDefaultNoteShare } from "utils/noteSharing";

export type AddNoteOptions = {
  pinned?: boolean;
  /** Voice note: Storage download URL. `text` then holds the transcription (may be empty). */
  audioUrl?: string;
};

type UseContactNotesReturn = {
  /** Own + org-shared teammates' notes, newest-first (the shared cache shape). */
  notes: DisplayUserNote[];
  /** True only on a true cold start (no preloaded cache, listener not fired). */
  isLoading: boolean;
  /** Whether the user is in an org — gates the per-note share toggle. */
  canShareWithOrg: boolean;
  addNote: (text: string, options?: AddNoteOptions) => Promise<void>;
  updateNote: (noteId: string, text: string) => Promise<void>;
  deleteNote: (noteId: string) => Promise<void>;
  setNotePinned: (noteId: string, pinned: boolean) => Promise<void>;
  setNoteOrgShare: (noteId: string, share: boolean) => Promise<void>;
};

export function useContactNotes(contactId: string | undefined): UseContactNotesReturn {
  const {
    user: { email: userEmail }
  } = useAuth();
  const { organizationId, organizations, organizationUsers } = useFirestore();

  // Warm start: the preload path already put the own notes into the cache.
  const cachedNotes = contactId
    ? getCached<DisplayUserNote[]>(notesCacheKey(contactId))
    : undefined;

  const [myNotes, setMyNotes] = useState<UserNote[]>([]);
  const [orgNotes, setOrgNotes] = useState<DisplayUserNote[]>([]);
  // Which contact the own-doc listener has hydrated (successfully snapshotted)
  // for — a plain boolean would leak truthiness across an in-place contact
  // switch (the reset effect only applies on the NEXT commit).
  const [hydratedFor, setHydratedFor] = useState<string | null>(null);
  const [listenerError, setListenerError] = useState(false);
  // Refs mirror the state for mutation-time reads (event handlers and frozen
  // async closures must see the LATEST values, not their render's snapshot).
  const myNotesRef = useRef<{ contactId: string | undefined; notes: UserNote[] }>({
    contactId,
    notes: []
  });
  const hydratedForRef = useRef<string | null>(null);
  const docIdRef = useRef<{ contactId: string; docId: string } | null>(null);
  // Serializes whole-doc writes from this hook instance (stable across renders,
  // so even stale closures share the same queue).
  const mutationQueueRef = useRef<Promise<unknown>>(Promise.resolve());

  // Reset per-contact state if the mounted page is reused for another contact.
  useEffect(() => {
    setMyNotes([]);
    setOrgNotes([]);
    setHydratedFor(null);
    setListenerError(false);
    myNotesRef.current = { contactId, notes: [] };
    hydratedForRef.current = null;
    docIdRef.current = null;
  }, [contactId]);

  // Listener 1 — the current user's own notes doc for this contact. Light
  // (single doc) — stays alive while the page is open (tabs keep mounted).
  useEffect(() => {
    if (!contactId || !userEmail) return undefined;
    const unsubscribe = db
      .collection(USER_NOTES_COLLECTION_NAME)
      .where("userId", "==", userEmail)
      .where("contactId", "==", contactId)
      .limit(1)
      .onSnapshot(
        (snapshot) => {
          if (!snapshot.empty) {
            const doc = snapshot.docs[0];
            const data = doc.data() as UserNotesDocument;
            docIdRef.current = { contactId, docId: doc.id };
            const parsed = (data.notes ?? []).map((note) => ({
              ...note,
              noteTime: parseNoteTime(note.noteTime)
            }));
            myNotesRef.current = { contactId, notes: parsed };
            setMyNotes(parsed);
          } else {
            docIdRef.current = null;
            myNotesRef.current = { contactId, notes: [] };
            setMyNotes([]);
          }
          hydratedForRef.current = contactId;
          setHydratedFor(contactId);
        },
        (error) => {
          console.error("useContactNotes: own-notes listener failed", error);
          // Do NOT mark hydrated: mutations must keep using the one-shot
          // read-modify-write path instead of rebuilding the doc from the
          // empty local state (whole-doc set() would destroy existing notes).
          setListenerError(true);
        }
      );
    return unsubscribe;
  }, [contactId, userEmail]);

  // Listener 2 — teammates' docs with notes shared to my org for this contact.
  useEffect(() => {
    if (!contactId || !userEmail || !organizationId) return undefined;
    const unsubscribe = db
      .collection(USER_NOTES_COLLECTION_NAME)
      .where("contactId", "==", contactId)
      .where("organizationIds", "array-contains", organizationId)
      .onSnapshot(
        (snapshot) => {
          const collected: DisplayUserNote[] = [];
          snapshot.docs.forEach((doc) => {
            const data = doc.data() as UserNotesDocument;
            if (data.userId === userEmail) return; // own doc handled by listener 1
            for (const note of data.notes ?? []) {
              // The doc-level organizationIds is a union over notes with mixed
              // shared+private — re-filter per note.
              if (!(note.organizationIds ?? []).includes(organizationId)) continue;
              collected.push({
                ...note,
                noteTime: parseNoteTime(note.noteTime),
                isMine: false,
                authorEmail: data.userId
              });
            }
          });
          setOrgNotes(collected);
        },
        (error) => {
          console.error("useContactNotes: org-notes listener failed", error);
        }
      );
    return unsubscribe;
  }, [contactId, userEmail, organizationId]);

  // Merge mine + teammates', resolve author names, newest-first.
  const notes = useMemo<DisplayUserNote[]>(() => {
    const nameByEmail = new Map<string, string>();
    for (const orgUser of organizationUsers ?? []) {
      if (orgUser.email) nameByEmail.set(orgUser.email, orgUser.name || orgUser.email);
    }
    const mine: DisplayUserNote[] = myNotes.map((note) => ({
      ...note,
      isMine: true,
      authorEmail: userEmail ?? ""
    }));
    const merged = sortNotesNewestFirst([...mine, ...orgNotes]);
    return merged.map((note) =>
      note.isMine ? note : { ...note, authorName: nameByEmail.get(note.authorEmail) ?? note.authorEmail }
    );
  }, [myNotes, orgNotes, organizationUsers, userEmail]);

  // Mirror the live merged list into the shared cache (overview quick-panel,
  // tab badge and preload all read this key). Gated on hydratedFor matching so
  // the commit where contactId changed (state still holds the PREVIOUS
  // contact's notes) can never write them under the new contact's key.
  useEffect(() => {
    if (!contactId || hydratedFor !== contactId) return;
    setCached(notesCacheKey(contactId), notes);
  }, [contactId, hydratedFor, notes]);

  // The actual mutation body — see the write-safety invariants in the header.
  const runMutation = useCallback(
    async (mutate: (current: UserNote[]) => UserNote[]): Promise<void> => {
      if (!contactId || !userEmail) return;
      const targetContactId = contactId;
      const isCurrent = (): boolean => myNotesRef.current.contactId === targetContactId;

      // Resolve the mutation base + target doc id.
      let base: UserNote[];
      let docId: string;
      if (isCurrent() && hydratedForRef.current === targetContactId) {
        base = myNotesRef.current.notes;
        const stored = docIdRef.current;
        docId =
          stored && stored.contactId === targetContactId
            ? stored.docId
            : userNotesDocId({ userId: userEmail, contactId: targetContactId });
      } else {
        // Not hydrated for the target contact — seed from the server (query,
        // not `.doc(deterministicId)`: legacy docs have auto-generated ids).
        const snapshot = await db
          .collection(USER_NOTES_COLLECTION_NAME)
          .where("userId", "==", userEmail)
          .where("contactId", "==", targetContactId)
          .limit(1)
          .get();
        if (!snapshot.empty) {
          const doc = snapshot.docs[0];
          docId = doc.id;
          base = ((doc.data() as UserNotesDocument).notes ?? []).map((note) => ({
            ...note,
            noteTime: parseNoteTime(note.noteTime)
          }));
        } else {
          docId = userNotesDocId({ userId: userEmail, contactId: targetContactId });
          base = [];
        }
      }

      const updated = mutate(base);
      if (updated === base) return;

      // Optimistic local update — only while the page still shows the target
      // contact (a post-switch save must not paint another contact's notes).
      if (isCurrent()) {
        myNotesRef.current = { contactId: targetContactId, notes: updated };
        setMyNotes(updated);
        docIdRef.current = { contactId: targetContactId, docId };
      }

      const docOrgIds = computeDocOrgIds(updated);
      const docData: UserNotesDocument = {
        userId: userEmail,
        contactId: targetContactId,
        notes: updated,
        // Conditional include — set() without merge drops the field when absent.
        ...(docOrgIds ? { organizationIds: docOrgIds } : {})
      };
      try {
        await db.collection(USER_NOTES_COLLECTION_NAME).doc(docId).set(docData);
      } catch (error) {
        console.error("useContactNotes: save failed", error);
        // Roll back only if our optimistic value is still the latest.
        if (isCurrent() && myNotesRef.current.notes === updated) {
          myNotesRef.current = { contactId: targetContactId, notes: base };
          setMyNotes(base);
        }
        throw error;
      }
    },
    [contactId, userEmail]
  );

  // Serialized optimistic mutation on the OWN notes array: local state (and,
  // via the mirror effect, the shared cache) updates immediately; rolled back
  // + the error rethrown (caller shows the snackbar) if the write fails.
  const mutateMyNotes = useCallback(
    (mutate: (current: UserNote[]) => UserNote[]): Promise<void> => {
      const run = mutationQueueRef.current.then(() => runMutation(mutate));
      mutationQueueRef.current = run.catch(() => undefined);
      return run;
    },
    [runMutation]
  );

  const addNote = useCallback(
    (text: string, options?: AddNoteOptions): Promise<void> => {
      const trimmed = text.trim();
      // A voice note may legitimately carry an empty transcription.
      if ((!trimmed && !options?.audioUrl) || !contactId) return Promise.resolve();
      // Shared with the author's offices by default (`utils/noteSharing.ts`).
      const newNote = withDefaultNoteShare(
        {
          id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
          note: trimmed,
          noteTime: new Date(),
          ...(options?.pinned ? { pinned: true } : {}),
          ...(options?.audioUrl ? { audioUrl: options.audioUrl } : {})
        },
        organizations
      );
      return mutateMyNotes((current) => [newNote, ...current]);
    },
    [contactId, mutateMyNotes, organizations]
  );

  const updateNote = useCallback(
    (noteId: string, text: string): Promise<void> =>
      mutateMyNotes((current) => {
        if (!current.some((note) => note.id === noteId)) return current;
        // Legacy write shape: editing bumps noteTime (moves the note to top).
        return current.map((note) =>
          note.id === noteId ? { ...note, note: text.trim(), noteTime: new Date() } : note
        );
      }),
    [mutateMyNotes]
  );

  const deleteNote = useCallback((noteId: string): Promise<void> => {
    let removedAudioUrl: string | undefined;
    return mutateMyNotes((current) => {
      const target = current.find((note) => note.id === noteId);
      if (!target) return current;
      removedAudioUrl = target.audioUrl;
      return current.filter((note) => note.id !== noteId);
    }).then(() => {
      // Best-effort: a deleted voice note's audio lives at a permanent tokened
      // public URL — revoke the underlying Storage object so it doesn't stay
      // downloadable forever. Never blocks/fails the note deletion itself.
      if (removedAudioUrl) {
        try {
          void storage
            .refFromURL(removedAudioUrl)
            .delete()
            .catch(() => undefined);
        } catch {
          // not a Storage URL — nothing to revoke
        }
      }
    });
  }, [mutateMyNotes]);

  const setNotePinned = useCallback(
    (noteId: string, pinned: boolean): Promise<void> =>
      mutateMyNotes((current) => {
        if (!current.some((note) => note.id === noteId)) return current;
        return current.map((note): UserNote => {
          if (note.id !== noteId) return note;
          if (pinned) return { ...note, pinned: true };
          // Absent == not pinned (schema convention) — drop the field on unpin.
          const { pinned: _drop, ...rest } = note;
          return rest as UserNote;
        });
      }),
    [mutateMyNotes]
  );

  // Toggle sharing one of the user's OWN notes: shared = every office the
  // author is in, private = nobody, the per-note field dropped entirely (never
  // `[]`) — `utils/noteSharing.ts`, shared with useListingNotes/useSellerNotes.
  const setNoteOrgShare = useCallback(
    (noteId: string, share: boolean): Promise<void> => {
      if (!organizationId) return Promise.resolve();
      return mutateMyNotes((current) => {
        if (!current.some((note) => note.id === noteId)) return current;
        return current.map((note) => (note.id === noteId ? applyNoteShare(note, share, organizations) : note));
      });
    },
    [mutateMyNotes, organizationId, organizations]
  );

  // Before the first listener tick for THIS contact, serve the preloaded cache
  // (no flash). Never fall back to `notes` here: on the contact-switch commit
  // the state still holds the previous contact's notes.
  const hydrated = hydratedFor === contactId;
  const effectiveNotes = hydrated ? notes : cachedNotes ?? [];

  return {
    notes: effectiveNotes,
    isLoading: !hydrated && cachedNotes === undefined && !listenerError,
    canShareWithOrg: !!organizationId,
    addNote,
    updateNote,
    deleteNote,
    setNotePinned,
    setNoteOrgShare
  };
}
